import useAxiosPrivate from "./useAxiosPrivate";
import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useUseCases } from "../context/UseCasesProvider";

const usePrefetchProducts = (apiUrl) => {
  const queryClient = useQueryClient();
  const axiosPrivate = useAxiosPrivate();
  const settingsInterface = useUseCases();

  // Function to obtain products by IDs
  const fetchProductsByIds = useCallback(
    async (ids, customerId) => {
      const cleanedIds = typeof ids === 'string' ? ids.replace(/['"]/g, '') : String(ids).replace(/['"]/g, '');
      const encodedIds = encodeURIComponent(cleanedIds);
      let query = `/${apiUrl}?product_id=${encodedIds}&source=internal`;
      if (customerId && customerId !== "None") {
        query += `&customer_id=${customerId}`;
      }
      const response = await axiosPrivate.get(query);
      return response.data.data || [];
    },
    [axiosPrivate, apiUrl],
  );

  const removeAccents = (text) => {
    return text.normalize("NFD").replaceAll(/[\u0300-\u036f]/g, "");
  };

  // Function to obtain products by keywords
  const fetchProductsByKeywords = useCallback(
    async (keywords, customerId, entityId) => {
      const normalizedKeywords = removeAccents(keywords);
      const encodedKeywords = encodeURIComponent(normalizedKeywords);
      let query = `/${apiUrl}?q=${encodedKeywords}`;
      if (customerId) {
        query += `&customer_id=${customerId}&source=internal`;
      }

      if (settingsInterface?.settings?.display?.product_select?.product_search_unavailable === false) {
        query += '&available=true';
      }

      if (settingsInterface?.settings?.display?.product_select?.product_search_entity_restriction === true && entityId) {
        query += `&entity_id_list=${encodeURIComponent(entityId)}`;
      }

      const response = await axiosPrivate.get(query);
      return response.data.data || [];
    },
    [axiosPrivate, apiUrl, settingsInterface],
  );

  // Generate a unique key for a product, accounting for variants
  const getProductKey = (product) => {
    if (product.product_variant_id) {
      return `${product.product_id}__${product.product_variant_id}`;
    }
    return product.product_id;
  };

  // Function to initialize product data with specific IDs
  const initAllProducts = useCallback(
    async (initialProductIds, customerId) => {
      const products = await fetchProductsByIds(initialProductIds, customerId);
      const existingProducts = queryClient.getQueryData(["allProducts"]) || [];
      // Merge: update existing products and add new ones (supporting variants)
      const productMap = new Map(existingProducts.map((p) => [getProductKey(p), p]));
      for (const product of products) {
        productMap.set(getProductKey(product), product);
      }
      queryClient.setQueryData(["allProducts"], Array.from(productMap.values()));
    },
    [queryClient, fetchProductsByIds],
  );

  // Function to add new products to the existing list
  const addProducts = useCallback(
    async (query, customerId, { onFetchStart, onFetchEnd, entityId } = {}) => {
      onFetchStart?.();
      let newProducts = [];

      if (Array.isArray(query)) {
        // Processing by IDs
        newProducts = await fetchProductsByIds(query, customerId);
      } else if (typeof query === "string") {
        // Processing by keywords
        newProducts = await fetchProductsByKeywords(query, customerId, entityId);
      }

      // Retrieve existing cache items
      const existingProductsData =
        queryClient.getQueryData(["allProducts"]) || [];

      let updatedProducts = [...existingProductsData];

      // Function to find an existing product by `product_secondary_id` or `product_id`
      const findExistingProductIndex = (newProduct) => {
        return updatedProducts.findIndex((existingProduct) => {
          // Check if `product_secondary_id` is present and product_id matches
          if (
            newProduct.hasOwnProperty("product_secondary_id") &&
            existingProduct.product_id ===
              newProduct.product_id
          ) {
            return true;
          }
          return false;
        });
      };

      newProducts.forEach((newProduct) => {
        const existingIndex = findExistingProductIndex(newProduct);

        if (existingIndex !== -1) {
          // Replace existing product with the new product
          updatedProducts[existingIndex] = newProduct;
        } else {
          // Add the new product if it doesn't already exist
          updatedProducts.push(newProduct);
        }
      });

      queryClient.setQueryData(["allProducts"], updatedProducts);
      onFetchEnd?.();
    },
    [queryClient, fetchProductsByIds, fetchProductsByKeywords],
  );

  return { initAllProducts, addProducts };
};

export default usePrefetchProducts;
